import type { ReactNode } from 'react';
import { cssTransition, ToastContainer, type ToastContentProps } from 'react-toastify';
import styled, { css } from 'styled-components';
import { ButtonWithIcon } from 'components/Button';
import Icon from 'components/Icon';
import type { ToTransientProps } from 'types/styled';
import { breakpoint, sizes, zIndex } from 'variables';

type ToastComponentProps = Partial<ToastContentProps> & {
    children?: ReactNode;
    type?: 'success' | 'error' | 'info' | 'warning';
};

const ToastElement = styled.div<Pick<ToTransientProps<ToastComponentProps>, '$type'>>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${({ theme }) => theme.Spacing8};
    padding: ${({ theme }) => theme.Spacing16};
    width: 100%;
    
    ${({ theme, $type }) => {
        switch ($type) {
            case 'success':
                return css`
                    color: ${theme.ColorTextOnSuccessLight};
                    background-color: ${theme.ColorBackgroundSuccessLight};
                `;
            case 'error':
                return css`
                    color: ${theme.ColorTextOnErrorLight};
                    background-color: ${theme.ColorBackgroundErrorLight};
                `;
            case 'warning':
                return css`
                    color: ${theme.ColorTextOnWarningLight};
                    background-color: ${theme.ColorBackgroundWarningLight};
                `;
            case 'info':
            default:
                return css`
                    color: ${theme.ColorTextOnInfoLight};
                    background-color: ${theme.ColorBackgroundInfoLight};
                `;
        }
    }};
`;

export function DefaultToastComponent(props: ToastComponentProps) {
    const { toastProps, data, isPaused, closeToast, type, children, ...restProps } = props;

    return (
        <ToastElement $type={type} {...restProps}>
            <div>{children}</div>
            <ButtonWithIcon
                variant="ghostOnLight"
                icon={<Icon type="Close" size={16} />}
                size="sm"
                onClick={closeToast}
            />
        </ToastElement>
    );
}

const StyledToastContainer = styled(ToastContainer)`
    --toastify-toast-min-height: 64px;
    --toastify-toast-padding: 0;
    --toastify-toast-shadow: none;
    --toastify-container-width: 100%;

    &&& {
        z-index: ${zIndex.growl};
        padding: 0;
    
        ${breakpoint.up('768px')} {
            --toastify-container-width: fit-content;
        }

        .Toastify__toast {
            align-items: stretch;
            margin: 0;
        }
    
        @keyframes slideInRight {
            from {
                transform: translate3d(100%, 0, 0);
            }
    
            to {
                transform: translate3d(0, 0, 0);
            }
        }
    
        @keyframes slideInDown {
            from {
                transform: translate3d(0%, -110%, 0);
            }
    
            to {
                transform: translate3d(0, 0, 0);
            }
        }
    
        .slideInRight {
            animation: slideInDown 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
    
            ${breakpoint.up('768px')} {
                animation: slideInRight 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
            }
        }
    
        @keyframes fadeOut {
            from {
                opacity: 1;
            }
    
            to {
                opacity: 0;
            }
        }
    
        .opacityOut {
            animation: fadeOut 0.2s ease-out both;
        }
    }
`;

const Wrapper = styled.div`
    .Toastify {
        margin-bottom: 0;
    }
`;

const right = cssTransition({
    enter: 'slideInRight',
    exit: 'opacityOut',
    collapseDuration: 250
});

export default function DefaultToastContainer() {
    return (
        <Wrapper>
            <StyledToastContainer
                aria-live="polite"
                aria-atomic="true"
                transition={right}
                hideProgressBar={true}
                autoClose={5000}
                icon={false}
                closeButton={() => null}
            />
        </Wrapper>
    );
}
